/**
 * Turning a contact entry from `ListContacts` into a row on the contacts screen.
 *
 * Shared 1:1 with iOS `native-apps/ios/Symphony/Store/ContactRows.swift` and Android
 * `native-apps/android/app/src/main/java/com/wix/symphony/ContactRows.kt`. The rules below are a
 * cross-platform contract; change one and change all three.
 *
 * The server decides everything that depends on data the client cannot see: whether a thread is
 * waiting on the viewer, whether a person needs attention, which open loop to surface, and when the
 * last activity was. What is left here is presentation that three platforms would otherwise each
 * invent — initials, the shortened relative time on a row, and the two sections the list is split
 * into.
 *
 * The chips are client-side on purpose. The endpoint takes no filter: "waiting on me" is derived
 * from a message's direction and is not filterable on the underlying store, so a server-side chip
 * could only trim the page it had already fetched and would report "none" for a person whose
 * waiting thread sat outside it. Every entry already carries `waitingOnMe` and `needsAttention`, so
 * filtering here is both correct and free.
 */

/** The subset of a `ContactCard` these rules read. */
export type ContactRowCard = {
  personId?: string;
  displayName?: string;
  labels?: string[];
  lastTouchAt?: string | Date;
  needsAttention?: boolean;
  attentionSummary?: string;
  openLoopCount?: number;
};

/** The subset of a `ContactWithOutreaches` these rules read. */
export type ContactRowEntry = {
  contact?: ContactRowCard;
  lastActivityAt?: string | Date;
  waitingOnMe?: boolean;
  waitingCount?: number;
  // The outreach threads (rows only, no message bodies — those are the person-scoped read) the list
  // carries per contact, so the "Needs you" partition can ask whether a contact has any active
  // conversation without a second fetch. See `hasNeedsYou`.
  outbound?: ChannelThread[];
  inbound?: ChannelThread[];
  /** Present for a contact the people book knows only by an address; the seen-key falls back to it. */
  recipientId?: string;
};

/**
 * The name to show for a contact — used by the list row AND the detail header, so the two never
 * disagree. A real display name wins; otherwise a reachable channel value (so a counterparty the
 * people book knows only by an address shows that address), and finally the localized "Unnamed
 * contact" — never a blank line beside an empty avatar. `unnamed` is `t('contacts.row.unnamed')`.
 * 1:1 with the native `contactDisplayName` helpers.
 */
export function contactDisplayName(
  card: { displayName?: string } | undefined,
  channels: { value?: string }[] | undefined,
  unnamed: string,
): string {
  return card?.displayName?.trim() || channels?.find((channel) => channel.value)?.value || unnamed;
}

/**
 * Whether the contact screen's Reach out can run: for anybody the screen could open at all — a person
 * in the book, or an address-only counterparty.
 *
 * It used to require a `recipientId`, which only exists once somebody has been messaged, so it disabled
 * the button for exactly the people the Contacts tab now lists after the outreach head: everyone in the
 * book nobody has written to yet. The button never addressed the recipient anyway — it seeds a chat
 * naming the person, and the agent resolves and creates the recipient when it sends. Shared 1:1 with
 * iOS and Android `canReachOut`.
 */
export function canReachOut(personId: string | undefined, recipientId: string | undefined): boolean {
  return !!personId || !!recipientId;
}

/**
 * The address the Reach out seed names beside the person, so the agent never has to ask which of two
 * same-named people was meant: the preferred channel's value, else the first non-empty one. Undefined
 * when there is none, or when it is the very string the name already shows (a nameless contact is shown
 * BY its address, and "+972… (+972…)" says nothing twice). Shared 1:1 with iOS and Android
 * `reachOutHandle`.
 */
export function reachOutHandle(name: string, channels: { value?: string; preferred?: boolean }[] | undefined): string | undefined {
  const usable = (channels ?? []).filter((channel) => channel.value?.trim());
  const handle = (usable.find((channel) => channel.preferred) ?? usable[0])?.value?.trim();
  if (!handle) return undefined;
  return handle.toLowerCase() === name.trim().toLowerCase() ? undefined : handle;
}

/**
 * Which chip the list is filtered by.
 *
 * There is ONE attention chip, and it is the same rule as the "Needs attention" SECTION below.
 * They used to be two — a `needs-attention` that read only the open loops and a `waiting` that read
 * only `waitingOnMe` — while the section of that name was the union. So the chip labelled "Needs
 * attention" selected strictly fewer people than the section labelled "Needs attention", on the same
 * screen. The two questions are still distinguishable per row, which is where they are legible:
 * `rowSubtitle` says "waiting on your reply" or names the loop.
 */
export type ContactChip = 'all' | 'new' | 'action-required' | 'needs-attention';

/**
 * Whether a person has NEW correspondence on this device: their last activity is later than the device's
 * seen-mark for them (`lib/contact-seen.ts`), or — never opened here — within the last day. Pure; the
 * same rule on all three surfaces, each with its own per-device mark.
 */
export function isNewContact(entry: { lastActivityAt?: string | Date }, seenAt: number | null, now: number): boolean {
  if (!entry.lastActivityAt) return false;
  const at = new Date(entry.lastActivityAt).getTime();
  if (!Number.isFinite(at)) return false;
  return at > (seenAt ?? now - 24 * 60 * 60 * 1000);
}

// Initials are deliberately NOT here. `lib/avatar-color.ts` already owns them app-wide — first letter
// of the first two words — and its own comment records that iOS and Android mirror it. A second rule
// on this screen would give the same person one monogram in chat and a different one in contacts.

/**
 * The short relative time a row shows — "2h", "3d", "5w".
 *
 * Deliberately terse and never "now": the row is scanned, not read, and a column that changes width
 * between "just now" and "2d" makes the list jitter. Anything under an hour is "now" only in the
 * long form below; here it collapses to minutes so the column stays two or three characters.
 *
 * Returns an empty string when there is no timestamp, which is a real state — a person nobody has
 * spoken to yet — and must render as blank rather than as the epoch.
 */
export function shortRelativeTime(at: string | Date | undefined, now: Date = new Date(), nowLabel?: string): string {
  const then = toDate(at);
  if (!then) return '';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  // Under a minute reads as "Now" when the caller passes the localized label (a just-sent message, a
  // fresh touch); callers with no label keep the terse `Ns` for back-compat.
  if (seconds < 60) return nowLabel ?? `${Math.max(1, seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 52) return `${weeks}w`;
  return `${Math.floor(days / 365)}y`;
}

/**
 * The Inbox card's timestamp — the terse short unit worn with a localized "ago" ("2d ago"), or the
 * localized "Now" under a minute (Figma `17937:265653`). Built on {@link shortRelativeTime} so the
 * unit ladder stays single-sourced; the caller supplies the localized `ago` template because this
 * module is i18n-free. Mirrors iOS/Android `agoRelativeTime`. Blank when there is no timestamp.
 */
export function agoRelativeTime(
  at: string | Date | undefined,
  now: Date = new Date(),
  nowLabel: string,
  ago: (short: string) => string,
): string {
  const short = shortRelativeTime(at, now, nowLabel);
  if (!short || short === nowLabel) return short;
  return ago(short);
}

/**
 * The long form the detail screen shows against "Last contacted" — "2 days ago".
 *
 * Separate from the short form rather than derived from it: the row needs a column that does not
 * move, and the detail row is prose. Sharing one function would force one of them to read wrong.
 */
export function longRelativeTime(at: string | Date | undefined, now: Date = new Date()): string {
  const then = toDate(at);
  if (!then) return '';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const units: [number, string][] = [
    [60, 'minute'],
    [60, 'hour'],
    [24, 'day'],
    [7, 'week'],
  ];
  let value = seconds;
  let label = 'second';
  for (const [size, name] of units) {
    if (value < size) break;
    value = Math.floor(value / size);
    label = name;
  }
  return `${value} ${label}${value === 1 ? '' : 's'} ago`;
}

/**
 * Whether an entry belongs under the given chip.
 *
 * The attention chip is `needsAttention` itself, so the chip and the section it shares a name with
 * can never disagree about who belongs in it.
 */
export function matchesChip(entry: ContactRowEntry, chip: ContactChip, isNew?: (entry: ContactRowEntry) => boolean): boolean {
  // The attention chip is the same rule as the top section it shares a name with (see `sectionsOf`),
  // so the chip and the section can never disagree about who belongs in it.
  if (chip === 'needs-attention' || chip === 'action-required') return contactTurn(entry) === 'needsYou';
  if (chip === 'new') return isNew ? isNew(entry) : false;
  return true;
}

/**
 * The two sections the design splits the list into.
 *
 * **The top section is now the people whose move it is — `contactTurn === 'needsYou'`** — not
 * `hasNeedsYou` ("do they have any thread that isn't COMPLETED"), which is what it used to ask.
 * That mattered the moment the rows started badging three states: a contact whose only live thread
 * is one WE are waiting on wears "Waiting on a reply", and under the old rule that row sat under a
 * "Needs Attention" header. A row whose badge contradicts the section it is filed in is worse than
 * either being wrong alone, because the reader cannot tell which one to believe.
 *
 * Everyone else falls to "All Contacts", and nobody appears twice. A THIRD section ("Waiting on
 * them") is what the design ultimately wants and is deliberately not here: it needs a section
 * string that does not exist in any of the 37 locales on any of the three surfaces, and the list
 * is restructured per-outreach in the next change anyway — so it lands there, with the one new key,
 * rather than being written twice.
 */
export function sectionsOf<T extends ContactRowEntry>(entries: T[]): { needsAttention: T[]; all: T[] } {
  // Generic so a caller holding a RICHER row keeps its own type through the split — the docs above
  // already promised that of `needsAttention`, and narrowing to `ContactRowEntry` here forced every
  // caller to cast the result back to the thing it passed in.
  const attention: T[] = [];
  const all: T[] = [];
  for (const entry of entries) {
    if (contactTurn(entry) === 'needsYou') attention.push(entry);
    else all.push(entry);
  }
  return { needsAttention: attention, all };
}

/**
 * Whether a contact matches a local search — a case-insensitive substring of the display name (or a
 * label). The map-home embed still filters its loaded list with this. The Outreach CONTACTS tab does
 * not: its search goes to the server (`useContactBook(search)`), which resolves against the whole people
 * book and DOES match people who already have outreach — verified against production, which is what
 * retired the client-side filter that used to stand in for it there. Shared 1:1 with iOS
 * `ContactRows.matchesSearch` and Android `ContactRows.matchesSearch`.
 */
export function matchesSearch(entry: ContactRowEntry, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = entry.contact?.displayName?.toLowerCase() ?? '';
  if (name.includes(q)) return true;
  return (entry.contact?.labels ?? []).some((label) => label.toLowerCase().includes(q));
}

/**
 * Whether an INBOX row matches the search — the same contact test as `matchesSearch` (display name or a
 * label) OR the shown last message. The inbox row is a channel's last thing said, so typing part of a
 * reply finds the row even when the contact's name doesn't match. Filtering here (per row) rather than
 * per contact is what lets a search hit one channel of a person without pulling in their other channels.
 * Shared 1:1 with iOS/Android `ContactRows.inboxRowMatchesSearch`.
 */
export function inboxRowMatchesSearch(
  row: { contact?: { displayName?: string; labels?: string[] }; lastMessage?: string },
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if ((row.contact?.displayName ?? '').toLowerCase().includes(q)) return true;
  if ((row.contact?.labels ?? []).some((label) => label.toLowerCase().includes(q))) return true;
  return (row.lastMessage ?? '').toLowerCase().includes(q);
}

/**
 * The empty BOOK — no contacts at all, as opposed to a no-results search or a failed/still-loading
 * read. It drives the illustrated empty state and the "Add a new contact" CTA on the Outreach screen:
 * a failed load or an empty search must NOT show it, or a user who actually has contacts (or whose
 * fetch just broke) would be told they have none and nudged to add one.
 *
 * Shared 1:1 with iOS `ContactRows.isEmptyBook` and Android `ContactRows.isEmptyBook`.
 */
export function isEmptyBook(ready: boolean, count: number, searching: boolean): boolean {
  return ready && count === 0 && !searching;
}

// One collator per locale, not one per comparison: `bookTail` sorts the one list on the screen that
// grows without bound, a page at a time.
const collators = new Map<string, Intl.Collator>();
function collatorFor(locale: string | undefined): Intl.Collator {
  const key = locale ?? '';
  let collator = collators.get(key);
  if (!collator) {
    // `base`: case- AND accent-insensitive — "álvaro" sorts with "alvaro", "bea" with "Bea".
    collator = new Intl.Collator(locale, { sensitivity: 'base' });
    collators.set(key, collator);
  }
  return collator;
}

/**
 * The rows the Contacts tab shows AFTER the outreach-derived head: the people book's own pages
 * (`/api/contact-book` with `includeBook` / `bookCursor`), minus everyone the head already shows.
 *
 * The head alone is only the people the user has exchanged outreach with, so a user whose book held
 * phonebook imports or agent-added people but no outreach at all saw an EMPTY Contacts tab while the
 * agent told them the person was "already in your contacts". The book closes that, and the head still
 * leads, because those are the people with something going on.
 *
 * - A book row whose `personId` matches ANY head entry is dropped: the book is the whole book, so it
 *   includes people the head already shows, and the head's copy is the richer one (threads, badge, time).
 * - The book is deduped by `personId`, first copy kept — pages are cursor-joined here, and a person on
 *   two pages must not render twice (or collide as a React key). A row with no `personId` is kept as is.
 * - Sorted by display name, case- AND accent-insensitive, locale-aware — the server's slug order is only
 *   roughly alphabetical, and does not follow a rename. A row with no name sorts after every named one,
 *   ordered among themselves by their first channel value (what the row shows in place of a name, see
 *   `contactDisplayName`); `personId` is the final tiebreak so equal names never swap between renders.
 *
 * Shared 1:1 with iOS `ContactRows.bookTail` and Android `ContactRows.bookTail`.
 */
export function bookTail<T extends ContactRowEntry & { channels?: { value?: string }[] }>(
  head: ContactRowEntry[],
  book: T[],
  locale?: string,
): T[] {
  const seen = new Set<string>();
  for (const entry of head) {
    const id = entry.contact?.personId;
    if (id) seen.add(id);
  }
  // Each row's sort key is derived ONCE, not inside the comparator.
  const keyed: { entry: T; named: boolean; key: string; personId: string }[] = [];
  for (const entry of book) {
    const personId = entry.contact?.personId ?? '';
    if (personId) {
      if (seen.has(personId)) continue;
      seen.add(personId);
    }
    const name = entry.contact?.displayName?.trim() ?? '';
    const key = name || entry.channels?.find((channel) => channel.value)?.value || '';
    keyed.push({ entry, named: !!name, key, personId });
  }
  const collator = collatorFor(locale);
  keyed.sort((a, b) => {
    if (a.named !== b.named) return a.named ? -1 : 1;
    const order = collator.compare(a.key, b.key);
    if (order !== 0) return order;
    return a.personId < b.personId ? -1 : a.personId > b.personId ? 1 : 0;
  });
  return keyed.map((row) => row.entry);
}

/** The Outreach screen's tabs. `calls` is drawn only behind `symphonyOutreachCallTab`, and is never the
 *  default. Shared 1:1 with iOS/Android `ContactRows.OutreachTab`. */
export type OutreachTab = 'contacts' | 'inbox' | 'calls';

/**
 * Which tab the Outreach screen opens on: the INBOX when anybody in the head has a conversation at all
 * — outbound or inbound, open OR closed, because a finished exchange is still correspondence the Inbox
 * lists — else CONTACTS. A user with no outreach yet has an Inbox with nothing in it, and opening them
 * onto an empty tab while their people sit one tap away is the screen hiding the only thing it has.
 *
 * The caller decides this ONCE, when real data first arrives, and never over a tab the reader has
 * already picked. Shared 1:1 with iOS/Android `ContactRows.defaultTab`.
 */
export function defaultTab(entries: ContactRowEntry[]): OutreachTab {
  return entries.some((entry) => (entry.outbound?.length ?? 0) > 0 || (entry.inbound?.length ?? 0) > 0) ? 'inbox' : 'contacts';
}

/**
 * Whether this entry belongs in the "Needs Attention" section.
 *
 * Exported as its own predicate so a caller holding a richer row than `ContactRowEntry` can
 * partition its own list with the same rule, instead of matching results back by identity.
 */
export function needsAttention(entry: ContactRowEntry): boolean {
  // ONLY the People service's verdict (an open loop / a question left hanging). `waitingOnMe` used to
  // count too, and it fires whenever the last message on a thread is the contact's — so a "confirmed,
  // see you tomorrow" and a "17 purchases so far" both wore "Action required" (a real report: twelve
  // rows flagged, one of them an actual ask). A reply that asks nothing is not an action.
  return Boolean(entry.contact?.needsAttention);
}

/** A reply came in and nobody has answered it — informational, not an action: the row says "Replied". */
export function repliedUnanswered(entry: ContactRowEntry): boolean {
  return Boolean(entry.waitingOnMe) && !needsAttention(entry);
}

// ── Whose move it is ───────────────────────────────────────────────────────────────────────────

/**
 * The THREE states an outreach thread can be in, and the only vocabulary the contact surfaces use.
 *
 * Shared 1:1 with iOS/Android `ContactRows.ThreadTurn`.
 *
 * This replaces two things that were each wrong in their own way. The row badge was `hasNeedsYou` —
 * "does this person have any thread that isn't COMPLETED" — which never looked at whose turn it was,
 * so a thread you had already answered and a thread that had been waiting nine weeks wore the same
 * "Waiting on you". And the per-thread badge was the raw `OutreachStatus` (Pending / In progress /
 * Done), a lifecycle value the reader cannot act on and which `outreach-rows.ts`'s own header calls
 * unreliable: "a `PENDING` thread had the user's reply already sent while an `IN_PROGRESS` one had
 * been stuck for 69 days".
 *
 * - `needsYou` — they spoke last and the thread is open. The only one that asks anything.
 * - `awaitingReply` — we spoke last and the thread is open. Live, but not the user's move.
 * - `closed` — the agent judged the exchange finished (`OutreachStatus.COMPLETED`).
 *
 * Measured on production over 2026-07-29 → 2026-09-27, `awaitingReply` is the single biggest bucket
 * and the one with no badge of its own today: 1,622 of the 2,875 directional two-plus-message threads
 * were sent and never answered, every one of them reading "Waiting on you". That is why this exists.
 */
export type ThreadTurn = 'needsYou' | 'awaitingReply' | 'closed';

/**
 * Whose move it is on ONE thread.
 *
 * Both inputs are server-set and already on every row: `status` (lowercased `OutreachStatus`) and
 * `waitingOnMe`, which symphony-bff derives from the last message's `direction` resolved against the
 * caller's own side. Nothing is inferred here, which is what lets a LIST row — which carries no
 * message bodies — badge itself exactly like the thread it opens.
 *
 * `closed` is tested FIRST and the order is load-bearing: a thread closes most often when an arriving
 * message is judged to ask nothing, which leaves THEIR message as the tail, so a closed thread very
 * commonly still reads `waitingOnMe`. (symphony-bff now clears that itself, but this must not depend
 * on a server fix having reached the row in front of it — an old cached payload is still a payload.)
 *
 * An unset status is NOT closed: the server sends empty rather than guessing, and filing an unknown
 * under `closed` hides a live thread in the group nobody opens.
 */
export function threadTurn(thread: { status?: string; waitingOnMe?: boolean }): ThreadTurn {
  if (thread.status?.toLowerCase() === 'completed') return 'closed';
  return thread.waitingOnMe ? 'needsYou' : 'awaitingReply';
}

/**
 * Whose move it is on a CONTACT — the worst of their threads, because a row is one badge over many
 * conversations and the one that asks something has to win. A person with an unanswered question and
 * four settled threads is a person you owe an answer.
 *
 * Returns `undefined` for a contact with no threads at all (a searched person nobody has messaged),
 * which is a real state the row renders as no badge rather than as `closed` — never contacted and
 * finished are not the same fact.
 *
 * Deduped by `outreachId` for the reason {@link groupThreads} documents: a self-outreach is its own
 * source AND target, so it arrives in both lists with `waitingOnMe` inverted between the copies, and
 * counting it twice would let the inverted copy decide the badge.
 */
export function contactTurn(entry: ContactRowEntry): ThreadTurn | undefined {
  const seen = new Set<string>();
  let sawOpen = false;
  let sawAny = false;
  for (const thread of [...(entry.outbound ?? []), ...(entry.inbound ?? [])]) {
    const id = thread.outreachId ?? '';
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    sawAny = true;
    const turn = threadTurn(thread);
    if (turn === 'needsYou') return 'needsYou';
    if (turn === 'awaitingReply') sawOpen = true;
  }
  if (!sawAny) return undefined;
  return sawOpen ? 'awaitingReply' : 'closed';
}

/** The badge copy for a turn. All three keys already ship in all 37 locales on all three surfaces —
 *  `contacts.row.replied` and `tasks.status.waiting` were translated and then rendered nowhere. */
export function turnLabelKey(turn: ThreadTurn): string {
  if (turn === 'needsYou') return 'contacts.row.waitingOnYou';
  return turn === 'awaitingReply' ? 'tasks.status.waiting' : 'contacts.row.replied';
}

/**
 * The subtitle under a name on a list row.
 *
 * Prefers what the person is waiting on the viewer for, because that is the actionable half; falls
 * back to the open loop the server picked. Empty when neither applies, and the row then shows the
 * name alone rather than a blank line.
 */
export function rowSubtitle(entry: ContactRowEntry): string {
  return entry.contact?.attentionSummary ?? '';
}

/**
 * The i18n key for the "waiting on you" subtitle, or null when the row has none.
 *
 * Split out because the subtitle is TWO different things: a fixed phrase we own and must translate,
 * and the server's own open-loop summary, which is already the user's language and must be shown
 * verbatim. Returning one string for both meant the fixed half shipped as hardcoded English — it
 * rendered in English in all 36 non-English bundles even though the key existed. Same shape as
 * `sourceLabelKey`: a key or null, never a sentence.
 */
export function rowSubtitleKey(entry: ContactRowEntry): string | null {
  return entry.waitingOnMe ? 'contacts.row.waitingOnYou' : null;
}

/**
 * The i18n key naming where a contact came from, or null when there is nothing to translate.
 *
 * `Person.source` is free text (`StringValue`, maxLength 40), NOT an enum — the four values below
 * are just the ones anything writes today (`phone` from the contacts import, `outreach` and
 * `calendar` from their consumers, `agent`). So a caller must render the RAW value when this
 * returns null: a closed map would blank out the row the day a fifth source appears, and this field
 * is one string literal in one service away from having one.
 */
export function sourceLabelKey(source: string | undefined): string | null {
  const known = ['phone', 'outreach', 'calendar', 'agent'];
  const raw = (source ?? '').trim().toLowerCase();
  // `phone-removed`: the user deleted the phonebook entry, and People kept the person because it carries
  // what the user or the agent learned about them. It still came from the phone, so it reads as the phone.
  const value = raw === 'phone-removed' ? 'phone' : raw;
  return known.includes(value) ? `contacts.detail.source.${value}` : null;
}

/**
 * A message with the outreach envelope's `Message from <name>: ` prefix removed.
 *
 * Every outreach body in production opens with it — it is addressed to the RECIPIENT, whose client
 * needs telling who is writing. On this screen the reader already knows: they are looking at that
 * person's own page, and each bubble is already on its speaker's side. So the prefix is pure noise
 * that costs a preview row most of its width.
 *
 * Deliberately narrow: it strips only that exact envelope, at the very start, with a short name.
 * Anything else is returned untouched — a body that merely contains a colon must not be truncated.
 */
export function strippedMessageText(text: string | undefined): string {
  const value = (text ?? '').trim();
  // BOTH forms occur, and roughly half the corpus is `Reply from`. Matching only `Message from`
  // stripped outbound turns and left every reply wearing its envelope.
  const match = /^(?:Message|Reply) from [^:\n]{1,100}:\s*/.exec(value);
  return match ? value.slice(match[0].length).trim() : value;
}

/**
 * The line under a thread row's date — the latest thing said on it.
 *
 * Secondary, because it cannot identify the thread: an outreach has no subject and one cannot be
 * derived (in production every body opens `Hi,` followed by the same "I'm <agent>, <owner>'s AI
 * assistant" boilerplate, so any first-line heuristic yields a list of identical rows). The row is
 * titled by its creation date instead — see `threadDateParts`.
 */
export function threadPreview(thread: { lastMessage?: string }): string {
  return strippedMessageText(thread.lastMessage);
}

/**
 * How a thread row titles itself: its real subject when it has one, else the date it was opened.
 *
 * The subject is what the assistant wrote into the message metadata when it composed the outreach
 * (`smsSubject`/`emailSubject`), served by the BFF. It is often ABSENT — the in-app channel writes
 * none — so the date fallback is the normal case, not an error path, and both have to read well.
 *
 * Empty only when there is neither, which is why the caller can render this directly.
 */
/*
 * ⚠️ WRITTEN-DOWN EXCEPTION — a FILE SHARE is not distinguishable on this screen, on ANY of the three
 * surfaces, and it is a DATA-CONTRACT gap rather than a decision.
 *
 * `Outreach.shared_file` is what makes a received outreach a share, and it is why the task card, the
 * island quick-view row and the outreach thread all title such a row by the FILE and offer to open it
 * (`outreachRowTitle` / `presentSharedFile` in `lib/outreach-rows.ts`, mirrored on both phones). The
 * contacts screens cannot do the same, because they are fed by a DIFFERENT read: symphony-bff's
 * `ListContacts` → `OutreachThreadRow`, whose fields are `outreachId · channel · lastMessage ·
 * updatedAt · waitingOnMe · status · createdAt · messages · subject`. There is **no shared-file field
 * on it at all**, and none on `OutreachThreadMessage` either — so from here a share is
 * indistinguishable from an ordinary thread, and the row titles itself by its subject-or-date like any
 * other. Verified on the generated client (common-lib `SymphonyBffClient/types.ts`) and by the absence
 * of any `sharedFile` reference in web's `contacts/`, iOS's `Features/Contacts/` or Android's
 * `features/Contact*.kt`.
 *
 * This is NOT "web has no screen for it" and it is not per-surface: all three read the same RPC and all
 * three are equally blind. Closing it is a VMR change — a `shared_file` field on `OutreachThreadRow`,
 * populated where the thread is assembled — after which this rule grows a share branch and all three
 * contacts screens get it at once. Do not fix it on one surface by cross-referencing `/api/outreaches`
 * (which DOES carry the notice): that would give web a name the phones cannot have, from a second read
 * the phones do not make, which is the drift the three-surface rule exists to stop.
 */
export function threadTitle(thread: { subject?: string; createdAt?: string | Date }, locale?: string): string {
  const subject = (thread.subject ?? '').trim();
  return subject || threadDateLabel(thread.createdAt, locale);
}

/**
 * The date a thread was opened, as the row's fallback title.
 *
 * Absolute, and carrying the TIME as well as the day, because it stands in for a subject and so
 * has to tell two rows apart. Several of the same person's outreaches routinely share a date — three
 * on one day in the data this was built against — and a relative label ("5w") collapses them
 * completely, which is the same failure as the boilerplate first line.
 *
 * Empty when there is no date, so a caller shows nothing rather than an epoch. iOS and Android
 * format the same choice with their own locale APIs (`DateFormatter` / `DateFormat`).
 */
export function threadDateLabel(at: string | Date | undefined, locale?: string): string {
  const date = toDate(at);
  if (!date) return '';
  return new Intl.DateTimeFormat(locale || undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/**
 * Whether a message opens a new run by its speaker — i.e. whether to draw a sender header on it.
 *
 * The chat idiom: consecutive messages from one side show the name once, on the first of the run.
 * Repeating it on every bubble is what makes a two-party thread read as a form rather than a
 * conversation. `index === 0` always opens a run.
 *
 * Who the speaker IS is not carried per message and does not need to be: an outreach has exactly two
 * ends, so `fromMe` plus the contact's own name names both. The server resolves `fromMe` against the
 * caller's side (the same message is FROM_SOURCE on both ends and means opposite things), so the
 * clients never re-derive it.
 */
export function startsSenderRun(messages: ThreadSpeaker[], index: number): boolean {
  if (index <= 0) return true;
  return speakerOf(messages[index]) !== speakerOf(messages[index - 1]);
}

/**
 * The channels an outreach can go out on, in the order the palette assigns them a colour.
 *
 * `channel` is a lowercased free string on the wire, not an enum — the four below are what anything
 * writes today. An unknown one is NOT dropped: it takes the neutral slot and still renders, the same
 * rule `sourceLabelKey` follows, because a fifth channel is one string literal away.
 */
export const CHANNEL_SLOTS = ['whatsapp', 'email', 'app', 'sms', 'gmail_connector'] as const;

export type ChannelSlot = (typeof CHANNEL_SLOTS)[number] | 'other';

/**
 * Which colour a channel wears. One slot per channel so the same channel is the same colour on every
 * surface and on every row — the point is that a thread's medium is recognisable at a glance, which
 * a single grey label could never do.
 *
 * Returns a SLOT, never a colour: the token layer resolves it, so dark mode and any future re-export
 * move all three surfaces at once. Shared 1:1 with the native `channelSlot`.
 */
export function channelSlot(channel: string | undefined): ChannelSlot {
  const value = (channel ?? '').trim().toLowerCase();
  return (CHANNEL_SLOTS as readonly string[]).includes(value) ? (value as ChannelSlot) : 'other';
}

/**
 * The label a channel shows.
 *
 * A known channel is translated; an unknown one renders its RAW value rather than being blanked,
 * for the same reason `sourceLabelKey` returns null instead of a default.
 */
export function channelLabelKey(channel: string | undefined): string | null {
  const slot = channelSlot(channel);
  return slot === 'other' ? null : `contacts.channel.${slot}`;
}

/** The subset of a message the speaker rules read. */
export type ThreadSpeaker = { fromMe?: boolean; senderName?: string };

/**
 * A stable identity for whoever wrote a message, used only to compare two of them.
 *
 * Prefers the envelope name over `fromMe`, because `fromMe` is WRONG on older messages: they carry
 * no direction, so the server attributes them to the other party and a `fromMe`-only comparison
 * folds a whole legacy thread into one run under the wrong speaker. The envelope survives there.
 */
function speakerOf(message: ThreadSpeaker | undefined): string {
  const name = (message?.senderName ?? '').trim();
  if (name) return `name:${name.toLowerCase()}`;
  return message?.fromMe ? 'side:me' : 'side:them';
}

/**
 * The name to show above a run, or empty to show none.
 *
 * Prefers the envelope's own name; falls back to the two names a thread has — the viewer and the
 * contact — since an outreach has exactly two ends. Empty when neither is known, and the bubble's
 * side then carries the attribution on its own.
 */
export function senderLabel(message: ThreadSpeaker | undefined, names: { me: string; them: string; myAgent?: string; theirAgent?: string }): string {
  // An outreach is two ASSISTANTS corresponding, not two people typing: this side's Maestro wrote
  // the outbound messages and the other side's agent wrote the replies. So the agent's name is the
  // honest attribution, and the person's name is the fallback for the very common case where the
  // counterparty runs no assistant (they are not a Symphony user) — there the message really is
  // from them, relayed.
  //
  // The envelope is deliberately NOT preferred here any more. It names the person the outreach is
  // FROM ("Message from Amir Harel"), which is who it is on behalf of, not who composed it.
  const mine = messageIsMine(message, names.me);
  const agent = (mine ? names.myAgent : names.theirAgent)?.trim();
  if (agent) return agent;

  const envelope = (message?.senderName ?? '').trim();
  if (envelope) return envelope;
  return (mine ? names.me : names.them).trim();
}

/**
 * Which side of the transcript a message sits on.
 *
 * The envelope wins over `fromMe` for the same reason `speakerOf` prefers it — on a legacy message
 * `fromMe` is simply wrong, and a chat view puts every one of those on the wrong side. When the
 * envelope names the viewer, the message is theirs whatever the missing direction implies.
 */
export function messageIsMine(message: ThreadSpeaker | undefined, myName: string): boolean {
  // `fromMe` is the server-resolved direction — trust it when it says the message is ours. The sender
  // name is only a FALLBACK for older messages that carry no direction (fromMe absent/false), which the
  // server attributes to the other party; a matching name pulls those back to our side. Checking the
  // name FIRST (as this did) mis-files our own messages as "theirs" whenever the sender name is not our
  // name — and it rarely is exactly our name: it is our AGENT's name, which need not equal the reader's
  // recipient name and changes the moment the agent is renamed (a message sent as "Señor Banana" then
  // read after a rename to "Señor Bananaush" flipped to the received side).
  if (message?.fromMe) return true;
  const envelope = (message?.senderName ?? '').trim().toLowerCase();
  const mine = myName.trim().toLowerCase();
  return !!envelope && !!mine && envelope === mine;
}

/**
 * The label for a file attached to a message.
 *
 * The size is deliberately optional and usually absent — nothing in production sets it — so this
 * returns the name alone rather than "name · 0 B". Bytes are formatted only when a real size arrived.
 */
export function attachmentLabel(attachment: { fileName?: string; size?: number | string }): string {
  const name = (attachment.fileName ?? '').trim();
  const bytes = typeof attachment.size === 'string' ? Number(attachment.size) : attachment.size;
  if (!bytes || !Number.isFinite(bytes) || bytes <= 0) return name;
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value >= 10 || unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${name} · ${rounded} ${units[unit]}`;
}

// ── The detail screen's thread groups ───────────────────────────────────────────────────────────

/** The subset of an outreach thread these rules read. */
export type ContactThread = {
  outreachId?: string;
  status?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  /** Server-set: the thread's last message came from the other end. Declared HERE rather than only on
   *  the inbox row because {@link threadTurn} reads it, and every surface that shows a thread now
   *  shows a turn — the list rows carry it already (`OutreachThreadRow.waiting_on_me`). */
  waitingOnMe?: boolean;
};

/** Which group a thread is filed under on the detail screen. */
export type ThreadGroup = 'active' | 'done';

/**
 * Which group a thread belongs to.
 *
 * Only an explicit `completed` is done. An outreach with **no** status is active, because an unset
 * status means "we do not know" — the server sends empty rather than guessing, and filing an
 * unknown under Done hides a live thread in the section nobody expands. Every outreach written
 * before the status field existed lands here, so this is the ordinary case on older threads rather
 * than an edge one.
 */
export function threadGroup(thread: ContactThread): ThreadGroup {
  return thread.status?.toLowerCase() === 'completed' ? 'done' : 'active';
}

/**
 * The detail screen's two groups, each newest-first.
 *
 * Ordered by when a thread last MOVED, while each group's header names when its threads STARTED —
 * the two dates answer different questions and the server sends both for exactly that reason.
 */
export function groupThreads(threads: ContactThread[]): {
  active: ContactThread[];
  done: ContactThread[];
} {
  const active: ContactThread[] = [];
  const done: ContactThread[] = [];
  // The caller hands us `outbound` concatenated with `inbound`, and the SAME outreach can be in
  // both: a self-outreach (the caller is its source AND its target — the weekly summaries Symphony
  // sends its own user) matches both of the server's pinned side queries, arriving twice with
  // `fromMe`/`waitingOnMe` inverted between the copies. Deduping here rather than at each of the
  // three call sites is what stops it rendering as two groups, once per speaker. The first copy
  // wins — `outbound` is passed first, and for an outreach the caller sent, "I said this" is the
  // reading that matches who wrote it.
  const seen = new Set<string>();
  for (const thread of threads) {
    const id = thread.outreachId ?? '';
    if (id && seen.has(id)) continue;
    if (id) seen.add(id);
    (threadGroup(thread) === 'done' ? done : active).push(thread);
  }
  const byNewest = (a: ContactThread, b: ContactThread) => (toDate(b.updatedAt)?.getTime() ?? 0) - (toDate(a.updatedAt)?.getTime() ?? 0);
  active.sort(byNewest);
  done.sort(byNewest);
  return { active, done };
}

// ── The flat outreach list ─────────────────────────────────────────────────────────────────────

/** One outreach as the contact screen and the Inbox list it — a CONVERSATION, not a channel. */
export type OutreachListRow = ChannelThread & {
  outreachId: string;
  lastMessage?: string;
  /** Whose move it is, resolved once here so the row, its badge and its section cannot disagree. */
  turn: ThreadTurn;
};

/** The three groups the flat list draws, each newest-first. */
export type OutreachSections = { needsYou: OutreachListRow[]; awaitingReply: OutreachListRow[]; closed: OutreachListRow[] };

/**
 * A contact's outreaches as a FLAT list, grouped by whose move it is.
 *
 * Shared 1:1 with iOS/Android `ContactRows.outreachSections`.
 *
 * **This replaces the per-CHANNEL roll-up** (`activeChannelCounts` + the viewer that stitched a
 * channel's outreaches into one scroll). That shape answered a question nobody asked: it listed
 * "WhatsApp · 2 conversations" under a heading called "Needs you", when both of those conversations
 * might be ones the USER was waiting on — the heading said the opposite of the truth. And it could
 * not name the actual ask, because a channel has no subject and no last message; only an outreach
 * does. A row per conversation can say what it is, what was last said, and whose move it is.
 *
 * Merging the two sides and deduping by `outreachId` is load-bearing for the same reason it is in
 * {@link contactTurn}: a self-outreach is its own source AND target, so it arrives in both lists with
 * `waitingOnMe` inverted between the copies. `outbound` is passed first and its copy wins — for an
 * outreach the user sent, "I said this" is the reading that matches who wrote it.
 *
 * Ordering is newest-first WITHIN each group rather than across the whole list, because the groups
 * are the priority: an ask from three weeks ago still outranks a reply we sent this morning, and a
 * single list sorted by recency buries it. A thread with no id is dropped — it cannot be opened, and
 * a row that goes nowhere is worse than an absent one.
 */
/**
 * The channel a conversation is DELIVERED on, normalised.
 *
 * An IN-APP outreach carries no delivery channel — the backend sends it empty — so it reads as `app`
 * rather than as a blank. `ChannelChip` renders nothing for an empty value, so an unnormalised in-app
 * conversation silently loses its badge while every other row keeps one, which reads as a rendering
 * fault rather than as "this one is in-app". That was reported once already, off a live screen.
 *
 * It is a shared RULE rather than an expression inlined at each call site precisely because the first
 * fix normalised only the list rows: the viewer header derived the channel straight off the raw thread
 * and kept rendering no chip, on all three surfaces. One function, so the next surface to show a
 * channel cannot get it wrong a third time. Shared 1:1 with iOS/Android `ContactRows.threadChannel`.
 *
 * ⚠️ The parameter is NOT optional, deliberately. "No thread at all" — the read still loading, or an
 * outreach id that resolves to nothing — is an UNKNOWN channel, not an in-app one, and an optional
 * parameter quietly collapsed the two: the viewer header rendered an "App" badge for a conversation
 * that did not exist. A caller holding a maybe-thread has to say what absence means, and for every
 * caller so far it means "draw no chip".
 */
export function threadChannel(thread: { channel?: string }): string {
  return (thread.channel ?? '').trim() || 'app';
}

export function outreachSections(entry: { outbound?: ChannelThread[]; inbound?: ChannelThread[] }): OutreachSections {
  const seen = new Set<string>();
  const out: OutreachSections = { needsYou: [], awaitingReply: [], closed: [] };
  for (const thread of [...(entry.outbound ?? []), ...(entry.inbound ?? [])]) {
    const outreachId = thread.outreachId ?? '';
    if (!outreachId || seen.has(outreachId)) continue;
    seen.add(outreachId);
    const turn = threadTurn(thread);
    out[turn].push({ ...thread, outreachId, turn, channel: threadChannel(thread) });
  }
  const byNewest = (a: OutreachListRow, b: OutreachListRow) =>
    (toDate(b.updatedAt)?.getTime() ?? toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.updatedAt)?.getTime() ?? toDate(a.createdAt)?.getTime() ?? 0);
  out.needsYou.sort(byNewest);
  out.awaitingReply.sort(byNewest);
  out.closed.sort(byNewest);
  return out;
}

/** The section headings, in render order. Every key already ships in all 37 locales on all three
 *  surfaces, and each one is the SAME string as its section's badge — so a row cannot sit under a
 *  heading that contradicts the badge it wears, which is exactly what "Needs Attention" used to do.
 *  The count renders beside it as a sibling, not inside the string: it is a number and a middot, and
 *  keeping it out means no key had to change and RTL flips it for free. */
export const OUTREACH_SECTIONS: { key: keyof OutreachSections; labelKey: string }[] = [
  { key: 'needsYou', labelKey: 'contacts.detail.needsYou' },
  { key: 'awaitingReply', labelKey: 'tasks.status.waiting' },
  { key: 'closed', labelKey: 'contacts.row.replied' },
];

/**
 * ONE of a contact's outreaches by id, whichever side it arrived on — what the viewer opens.
 *
 * Searches `outbound` first so a self-outreach (its own source AND target) resolves to the copy whose
 * `waitingOnMe` matches who wrote it, the same precedence {@link outreachSections} uses.
 */
export function findOutreach<T extends { outreachId?: string }>(
  entry: { outbound?: T[]; inbound?: T[] },
  outreachId: string,
): T | undefined {
  if (!outreachId) return undefined;
  return [...(entry.outbound ?? []), ...(entry.inbound ?? [])].find((t) => t.outreachId === outreachId);
}

/**
 * The one conversation to open straight from a contact ROW, or null when there is not exactly one.
 *
 * The outreach counterpart of the old `singleActiveChannel`: a contact with a single conversation has
 * one obvious thing to open, so the row opens it in the viewer instead of making the reader take a
 * second step through a screen that lists exactly one row. A contact with several has no single
 * answer, so the row opens the contact screen to choose from.
 *
 * Counts every conversation, closed ones included — because the list the reader would otherwise land
 * on shows those too. "Exactly one" has to mean the same thing here as the number of rows they would
 * see, or the shortcut fires on a screen that turns out to have four entries.
 */
export function singleOutreachId(entry: { outbound?: ChannelThread[]; inbound?: ChannelThread[] }): string | null {
  const all = Object.values(outreachSections(entry)).flat();
  return all.length === 1 ? all[0].outreachId : null;
}

/** The subset of a thread the "Needs you" channel roll-up reads — the group's threads plus their channel. */
export type ChannelThread = ContactThread & { channel?: string };






/** A thread as the INBOX list reads it: the channel roll-up fields plus what a row shows — the last
 *  thing said. `waitingOnMe` now comes from {@link ContactThread}, which every thread carries. */
export type InboxThread = ChannelThread & { lastMessage?: string };

/** One inbox entry (a contact and its threads) as the list carries it. Generic over the card so a
 *  caller holding a richer card (web's `WebContactCard`, with the avatar URL) keeps it on the row. */
export type InboxEntry<C extends { personId?: string } = ContactRowCard> = {
  contact?: C;
  /** The contact's addresses. Read for ONE thing: the display fallback for a contact with no name.
   *  Without it two address-only people both read "Unnamed contact" — on the row and, worse, in the
   *  contact FILTER, where two options with the same label cannot be told apart at all. */
  channels?: { value?: string }[];
  /** Present for a contact the people book knows only by an address (no `Person`); the row opens by it
   *  when there is no person id, mirroring #10660's recipient identity on the Contacts list. */
  recipientId?: string;
  outbound?: InboxThread[];
  inbound?: InboxThread[];
};

/** One inbox ROW — the last message of one contact on ONE channel. Tapping it opens the viewer on that
 *  contact's identity ({@link personId} or, address-only, {@link recipientId}) + channel — the same
 *  conversation the contact detail's "Needs you" rows open. */
export type InboxRow<C extends { personId?: string } = ContactRowCard> = {
  personId: string;
  /** The address-only identity — set (with an empty `personId`) for a contact the book knows only by an
   *  address, so the row opens the same recipient-keyed conversation the Contacts list does. */
  recipientId?: string;
  contact?: C;
  channel: string;
  lastMessage: string;
  lastActivityAt?: string | Date;
  waitingOnMe: boolean;
  /** Whose move it is on this row — what the badge reads. `waitingOnMe` stays beside it because the
   *  section split uses it; the two agree by construction (`turn === 'needsYou'` iff `waitingOnMe`). */
  turn: ThreadTurn;
  /** The conversation this row IS. The row opens it directly — there is no channel roll-up to resolve. */
  outreachId: string;
  /** The contact's first address, carried so an unnamed contact can be named by it. See
   *  {@link inboxRowName}. */
  address?: string;
};

/**
 * What to call the contact on an inbox row: their name, else their address, else "unnamed".
 *
 * The address arm is the point. The row used to pass no channels to {@link contactDisplayName}, so the
 * fallback could not apply and every address-only contact read "Unnamed contact". On a ROW that is
 * merely unhelpful — the message text below it still distinguishes them. In the contact FILTER it is a
 * defect: two options rendering the same label cannot be told apart, so the reader cannot know which
 * one they are selecting. One helper, so the row and the filter can never disagree about a name.
 */
export function inboxRowName<C extends { personId?: string; displayName?: string }>(
  row: { contact?: C; address?: string },
  unnamed: string,
): string {
  return contactDisplayName(row.contact, row.address ? [{ value: row.address }] : undefined, unnamed);
}

/**
 * The Inbox list: the last message of every contact on every CHANNEL, split "Needs attention" (any
 * thread on the channel awaiting the user) first, then the rest — each newest-first.
 *
 * One row per (contact, channel): a contact reachable on two channels shows twice. Unlike the detail
 * screen's "Needs you" roll-up (`activeChannelCounts`), this is NOT limited to active outreaches — the
 * inbox is all correspondence — so a channel whose threads are all completed still shows its last word.
 * Threads are deduped by `outreachId` (a self-outreach arrives in both `outbound` and `inbound`), the
 * empty in-app channel is bucketed to `app`, the row's message/time come from the channel's most
 * recently moved thread, and `waitingOnMe` is true if ANY thread on the channel is.
 *
 * Pure; shared 1:1 with iOS/Android `ContactRows.inboxRows`.
 */
export function inboxRows<C extends { personId?: string }>(entries: InboxEntry<C>[]): InboxRow<C>[] {
  const rows: InboxRow<C>[] = [];
  for (const entry of entries) {
    const personId = entry.contact?.personId ?? '';
    const recipientId = entry.recipientId ?? '';
    // Keep a contact the book knows only by an ADDRESS — it has no person id but a real conversation.
    // Only a row with NEITHER identity is unopenable, and dropped.
    if (!personId && !recipientId) continue;
    // ONE ROW PER OUTREACH. This used to roll the outreaches up per CHANNEL and show the newest one's
    // text, which meant a person with three live WhatsApp conversations appeared once, under whichever
    // of them had moved last — the other two were unreachable from here and their state was invisible.
    // A channel is not a conversation; it is how a conversation was delivered.
    for (const row of Object.values(outreachSections(entry)).flat()) {
      rows.push({
        personId,
        recipientId: personId ? undefined : recipientId,
        contact: entry.contact,
        outreachId: row.outreachId,
        channel: row.channel ?? 'app', // `outreachSections` already normalised in-app to `app`
        lastMessage: threadPreview(row),
        lastActivityAt: row.updatedAt ?? row.createdAt,
        waitingOnMe: row.turn === 'needsYou',
        turn: row.turn,
        address: entry.channels?.find((channel) => channel.value)?.value,
      });
    }
  }
  // FLAT and newest-first. The caller groups by `turn` through {@link OUTREACH_SECTIONS}, exactly as the
  // contact screen does — this used to return a `{ needsAttention, all }` pair, which was the two-way
  // `waitingOnMe` split and could not express the three states the rows were already badged with. The
  // Inbox and the contact screen now read one set of groups and one set of headings.
  return rows.sort((a, b) => (toDate(b.lastActivityAt)?.getTime() ?? 0) - (toDate(a.lastActivityAt)?.getTime() ?? 0));
}

/**
 * The Inbox's CONTACT filter options — one per contact present in the rows, in first-seen order.
 *
 * The labels have to be distinguishable, which is the whole reason this is a rule rather than a `map`.
 * Naming each option by `inboxRowName` alone is not enough: that answers with the display name when
 * there is one, so two different people who are both called "Dana Levi" produce two identical options
 * and the reader cannot tell which one they are about to select. An address-only pair is already
 * separated by the address fallback; a same-NAME pair is not.
 *
 * So a colliding label — and ONLY a colliding label — gets its address appended. Appending it to every
 * option would make the ordinary case noisy for a problem it does not have, and the ordinary case is
 * nearly all of them. Two contacts who share a name AND have no address between them are genuinely
 * indistinguishable from the data in hand; nothing here invents a difference.
 *
 * Shared 1:1 with iOS/Android `ContactRows.inboxContactOptions`.
 */
export function inboxContactOptions<C extends { personId?: string; displayName?: string }>(
  rows: { personId: string; recipientId?: string; contact?: C; address?: string }[],
  unnamed: string,
): { key: string; label: string }[] {
  const byKey = new Map<string, { label: string; address?: string }>();
  for (const row of rows) {
    const key = row.personId || row.recipientId || '';
    if (!key || byKey.has(key)) continue;
    byKey.set(key, { label: inboxRowName(row, unnamed), address: row.address });
  }
  const times = new Map<string, number>();
  for (const { label } of byKey.values()) times.set(label, (times.get(label) ?? 0) + 1);
  return [...byKey].map(([key, { label, address }]) => ({
    key,
    label: (times.get(label) ?? 0) > 1 && address && address !== label ? `${label} · ${address}` : label,
  }));
}

/** The flat inbox rows grouped by whose move it is — the same three groups, in the same order, that
 *  {@link outreachSections} gives the contact screen. Order within a group is the caller's (newest
 *  first), because the grouping is the priority and recency only breaks ties inside it. */
export function groupInboxRows<C extends { personId?: string }>(rows: InboxRow<C>[]): Record<ThreadTurn, InboxRow<C>[]> {
  const out: Record<ThreadTurn, InboxRow<C>[]> = { needsYou: [], awaitingReply: [], closed: [] };
  for (const row of rows) out[row.turn].push(row);
  return out;
}

function toDate(at: string | Date | undefined): Date | undefined {
  if (!at) return undefined;
  const date = at instanceof Date ? at : new Date(at);
  return Number.isNaN(date.getTime()) ? undefined : date;
}
